import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check, X, ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { SEO, SITE_URL } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { useContent } from "../lib/useContent";

export default function TimeshareDetail() {
  const { slug } = useParams();
  const { data: ts, loading, notFound } = useContent(`/timeshares/${slug}`, [slug]);
  if (loading) return <PageLoader />;
  if (notFound || !ts) return <Navigate to="/timeshare-reviews" replace />;

  const next = ts.next;

  const memberReviews = ts.memberReviews || [];
  const memberReviewsLd = memberReviews.length && {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ts.name,
    review: memberReviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      datePublished: r.date,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      name: r.title,
      reviewBody: r.body,
    })),
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Review",
      itemReviewed: { "@type": "Organization", name: ts.name },
      reviewRating: { "@type": "Rating", ratingValue: ts.rating, bestRating: 5, worstRating: 1 },
      author: { "@type": "Organization", name: "Travel Club Navigator Group" },
      publisher: { "@type": "Organization", name: "Travel Club Navigator Group" },
      reviewBody: ts.summary,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Timeshare Reviews", item: `${SITE_URL}/timeshare-reviews` },
        { "@type": "ListItem", position: 3, name: ts.name, item: `${SITE_URL}/timeshare-reviews/${ts.slug}` },
      ],
    },
    ...(memberReviewsLd ? [memberReviewsLd] : []),
  ];

  return (
    <>
      <SEO
        title={`${ts.name} Review: How It Works, Points & Fees`}
        description={`${ts.name} review — ${ts.verdict}. ${ts.summary}`}
        path={`/timeshare-reviews/${ts.slug}`}
        image={ts.image}
        keywords={["Timeshare Reviews", ts.name, "Timeshare Pros and Cons", "Vacation Club Review"]}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative flex min-h-[60vh] items-end overflow-hidden">
        <img src={ts.image} alt={ts.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-5xl px-5 pb-14 pt-32 lg:px-8">
          <Link
            to="/timeshare-reviews"
            data-testid="timeshare-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> All Timeshare Reviews
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-1.5 text-sm font-bold text-navy backdrop-blur">
                <Star className="h-4 w-4 fill-accent text-accent" />
                {ts.rating.toFixed(1)} / 5
              </span>
              <span className="rounded-full bg-primary px-3.5 py-1.5 text-sm font-semibold text-white">
                {ts.verdict}
              </span>
            </div>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              {ts.name}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-slate-200">{ts.tagline}</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        <Reveal>
          <Overline>The Overview</Overline>
          <p className="mt-4 font-serif-accent text-2xl leading-relaxed text-navy sm:text-3xl">
            {ts.summary}
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-12 grid gap-6 rounded-3xl card-3d p-8 sm:grid-cols-2">
            {ts.scores.map((s) => (
              <div key={s.label}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-navy">{s.label}</span>
                  <span className="font-display text-lg font-bold text-secondary">{s.value}</span>
                </div>
                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-secondary to-primary"
                    style={{ width: `${s.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-secondary/20 bg-secondary/5 p-8">
              <h3 className="font-display text-xl font-bold text-navy">What we liked</h3>
              <ul className="mt-5 space-y-3">
                {ts.pros.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-0.5 h-5 w-5 flex-none text-secondary" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-3xl border border-primary/20 bg-primary/5 p-8">
              <h3 className="font-display text-xl font-bold text-navy">Watch out for</h3>
              <ul className="mt-5 space-y-3">
                {ts.cons.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-slate-700">
                    <X className="mt-0.5 h-5 w-5 flex-none text-primary" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <div className="mt-8 flex gap-5 rounded-3xl bg-navy p-8 text-white sm:p-10">
            <Quote className="h-10 w-10 flex-none text-accent" />
            <div>
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-accent">
                The Bottom Line
              </h3>
              <p className="mt-3 font-serif-accent text-xl leading-relaxed sm:text-2xl">
                {ts.bottomLine}
              </p>
            </div>
          </div>
        </Reveal>

        {/* Member Reviews */}
        {memberReviews.length > 0 && (
          <Reveal delay={0.05}>
            <div className="mt-14" data-testid="timeshare-member-reviews">
              <Overline>From the Community</Overline>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
                {ts.name}: Owner Reviews
              </h2>
              {ts.reviewDisclosure && (
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
                  {ts.reviewDisclosure}
                </p>
              )}
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {memberReviews.map((r, i) => (
                  <div
                    key={`${r.name}-${i}`}
                    data-testid={`timeshare-review-${ts.slug}-${i}`}
                    className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
                  >
                    <div className="flex items-center gap-1" aria-label={`${r.rating} out of 5 stars`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`h-4 w-4 ${
                            n <= r.rating ? "fill-accent text-accent" : "text-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <h3 className="mt-3 font-display text-lg font-bold text-navy">{r.title}</h3>
                    <p className="mt-2 flex-1 leading-relaxed text-slate-600">"{r.body}"</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
                      <span className="font-semibold text-navy">{r.name}</span>
                      {r.location && <span>• {r.location}</span>}
                      {r.memberSince && <span>• Owner since {r.memberSince}</span>}
                      {r.date && <span>• {r.date}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.05}>
          <Link
            to={`/timeshare-reviews/${next.slug}`}
            data-testid="timeshare-next-link"
            className="group mt-10 flex items-center justify-between rounded-3xl card-3d p-6"
          >
            <span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Next review
              </span>
              <span className="mt-1 block font-display text-xl font-bold text-navy">
                {next.name}
              </span>
            </span>
            <ArrowRight className="h-6 w-6 text-primary transition-transform duration-200 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
